import React from "react";
import styled from "styled-components";
import { T } from "../../theme";
import { TESTIMONIALS, FOUNDER } from "../../data/careers";
import Reveal from "../Reveal";
import { Band, Shell, Eyebrow, Heading, Lead, Head } from "./ui";

const Layout = styled.div`
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: 16px;
  align-items: stretch;

  @media (max-width: 860px) { grid-template-columns: 1fr; }
`;

/* ── mensagem do fundador ── */
const FounderCard = styled.div`
  position: relative;
  background: linear-gradient(160deg, #14101f 0%, #0c0a14 100%);
  border: 1.5px solid rgba(155, 81, 255, 0.22);
  border-radius: 20px;
  padding: clamp(26px, 4vw, 38px);
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(ellipse 60% 50% at 90% 0%, rgba(155, 81, 255, 0.18), transparent 60%);
    pointer-events: none;
  }
`;

const Quote = styled.blockquote`
  position: relative;
  font-size: clamp(1rem, 1.9vw, 1.18rem);
  font-weight: 500;
  line-height: 1.6;
  color: #ede9f8;
  letter-spacing: -0.01em;

  &::before {
    content: "\\201C";
    display: block;
    font-size: 3.4rem;
    line-height: 0.6;
    color: var(--purple);
    margin-bottom: 12px;
  }
`;

const FounderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 13px;
  margin-top: 26px;
  position: relative;
`;

const Avatar = styled.div`
  width: 46px;
  height: 46px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.86rem;
  font-weight: 800;
  color: #fff;
  background: linear-gradient(135deg, #9b51ff, #7c3aed);
  box-shadow: 0 4px 14px rgba(124, 58, 237, 0.45);
`;

const AvatarImg = styled.img`
  width: 46px;
  height: 46px;
  border-radius: 50%;
  flex-shrink: 0;
  object-fit: cover;
  box-shadow: 0 4px 14px rgba(124, 58, 237, 0.45);
  border: 1.5px solid rgba(155, 81, 255, 0.5);
`;

const FName = styled.p`
  font-size: 0.9rem;
  font-weight: 800;
  color: #fff;
`;

const FRole = styled.p`
  font-size: 0.76rem;
  color: rgba(255, 255, 255, 0.55);
  margin-top: 2px;
`;

/* ── depoimentos ── */
const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TCard = styled.figure`
  background: ${T.card};
  border: 1.5px solid ${T.border};
  border-radius: 16px;
  padding: 20px;
  transition: border-color 0.16s, transform 0.16s;

  &:hover {
    border-color: color-mix(in srgb, var(--purple) 30%, transparent);
    transform: translateX(3px);
  }
`;

const TText = styled.blockquote`
  font-size: 0.86rem;
  color: ${T.text};
  line-height: 1.6;
  margin-bottom: 14px;
`;

const TWho = styled.figcaption`
  display: flex;
  align-items: center;
  gap: 11px;
`;

const TAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.72rem;
  font-weight: 800;
  color: #fff;
  background: ${(p) => p.$c || "var(--purple)"};
`;

const TName = styled.p`
  font-size: 0.8rem;
  font-weight: 700;
  color: ${T.text};
`;

const TRole = styled.p`
  font-size: 0.72rem;
  color: ${T.muted2};
  margin-top: 1px;
`;

export default function Life() {
  return (
    <Band id="vida" aria-labelledby="life-title">
      <Shell>
        <Reveal>
          <Head>
            <Eyebrow>Vida na Guebly</Eyebrow>
            <Heading id="life-title">Gente que constrói, não que só ocupa cadeira.</Heading>
            <Lead>
              Nascemos numa escola pública na periferia de São Paulo. Carregamos isso como
              força: aqui, talento de qualquer origem tem espaço para fazer acontecer.
            </Lead>
          </Head>
        </Reveal>

        <Reveal>
          <Layout>
            <FounderCard>
              <Quote>{FOUNDER.message}</Quote>
              <FounderRow>
                {FOUNDER.photo ? (
                  <AvatarImg src={FOUNDER.photo} alt={`Foto de ${FOUNDER.name}`} loading="lazy" />
                ) : (
                  <Avatar aria-hidden="true">{FOUNDER.initials}</Avatar>
                )}
                <div>
                  <FName>{FOUNDER.name}</FName>
                  <FRole>{FOUNDER.role}</FRole>
                </div>
              </FounderRow>
            </FounderCard>

            <Stack>
              {TESTIMONIALS.map((t, i) => (
                <TCard key={i}>
                  <TText>“{t.quote}”</TText>
                  <TWho>
                    <TAvatar $c={t.color} aria-hidden="true">{t.initials}</TAvatar>
                    <div>
                      <TName>{t.name}</TName>
                      <TRole>{t.role}</TRole>
                    </div>
                  </TWho>
                </TCard>
              ))}
            </Stack>
          </Layout>
        </Reveal>
      </Shell>
    </Band>
  );
}
